import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../utils/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { CurrencyInput } from "../components/ui/currency-input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";
import { Plus, Trash2, Search, Receipt, Printer } from "lucide-react";
import { SearchableSelect } from "../components/ui/searchable-select";



const Collections = () => {
  const [collections, setCollections] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: "",
    date: new Date().toISOString().split("T")[0],
    amount: "",
    method: "cash",
    account_id: "",
    notes: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [collRes, custRes, cashRes, bankRes] = await Promise.all([
        axios.get(`${API}/collections`),
        axios.get(`${API}/customers/active/list`),
        axios.get(`${API}/cash-accounts`),
        axios.get(`${API}/bank-accounts`),
      ]);
      setCollections(collRes.data);
      setCustomers(custRes.data);
      setCashAccounts(cashRes.data);
      setBankAccounts(bankRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (submitting) {
      return;
    }
    
    if (!formData.customer_id || formData.amount <= 0) {
      toast.error("Müşteri ve tutar zorunludur");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/collections`, formData);
      toast.success("Tahsilat kaydedildi");
      setDialogOpen(false);
      resetForm();
      fetchData();
      
      // 4 second cooldown before allowing next submission
      setTimeout(() => {
        setSubmitting(false);
      }, 4000);
    } catch (error) {
      console.error("Failed to save:", error);
      toast.error("İşlem başarısız");
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu tahsilatı silmek istediğinizden emin misiniz?")) return;

    try {
      await axios.delete(`${API}/collections/${id}`);
      toast.success("Tahsilat silindi");
      fetchData();
    } catch (error) {
      console.error("Failed to delete:", error);
      toast.error("Silme işlemi başarısız");
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: "",
      date: new Date().toISOString().split("T")[0],
      amount: "",
      method: "cash",
      account_id: "",
      notes: "",
    });
  };

  const handlePrint = (coll) => {
    setSelectedCollection(coll);
    setPrintDialogOpen(true);
  };

  const getAccounts = () => {
    if (formData.method === "cash") return cashAccounts;
    if (formData.method === "bank") return bankAccounts;
    return [];
  };

  const filteredCollections = collections.filter(
    (c) =>
      c.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.receipt_no.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const handlePagePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6 print:space-y-4" data-testid="collections-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Tahsilat
          </h1>
          <p className="text-slate-500 mt-1">Müşterilerden tahsilat kayıtları</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handlePagePrint} className="no-print">
            <Printer className="h-4 w-4 mr-2" /> Yazdır
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-collection-btn">
                <Plus className="h-5 w-5 mr-2" />
                Yeni Tahsilat
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Yeni Tahsilat</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Müşteri *</Label>
                <SearchableSelect
                  options={customers.map((c) => ({
                    value: c.id,
                    label: c.name,
                    subtitle: `Bakiye: ₺${c.balance.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`,
                  }))}
                  value={formData.customer_id}
                  onChange={(value) => setFormData({ ...formData, customer_id: value })}
                  placeholder="Müşteri seçin"
                  searchPlaceholder="Müşteri ara..."
                  emptyMessage="Müşteri bulunamadı."
                  data-testid="coll-customer-select"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tarih</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tutar *</Label>
                  <CurrencyInput
                    value={formData.amount}
                    onChange={(val) => setFormData({ ...formData, amount: val })}
                    placeholder="Tutar girin"
                    data-testid="coll-amount-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Ödeme Yöntemi</Label>
                <Select
                  value={formData.method}
                  onValueChange={(value) => setFormData({ ...formData, method: value, account_id: "" })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Nakit</SelectItem>
                    <SelectItem value="bank">Banka</SelectItem>
                    <SelectItem value="credit_card">Kredi Kartı</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(formData.method === "cash" || formData.method === "bank") && (
                <div className="space-y-2">
                  <Label>Hesap</Label>
                  <Select
                    value={formData.account_id}
                    onValueChange={(value) => setFormData({ ...formData, account_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Hesap seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {getAccounts().map((acc) => (
                        <SelectItem key={acc.id} value={acc.id}>{acc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label>Notlar</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Ek notlar"
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} disabled={submitting}>
                  İptal
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="coll-save-btn" disabled={submitting}>
                  {submitting ? "Kaydediliyor..." : "Kaydet"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Tahsilat ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0 md:p-0">
          {filteredCollections.length > 0 ? (
            <>
              {/* Mobile Card View */}
              <div className="md:hidden divide-y divide-slate-100">
                {filteredCollections.map((coll) => (
                  <div key={coll.id} className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-slate-900">{coll.customer_name}</p>
                        <p className="text-xs font-mono text-slate-500">{coll.receipt_no}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handlePrint(coll)} className="h-8 w-8 p-0">
                          <Printer className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(coll.id)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm text-slate-500">{coll.date}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        coll.method === "cash" ? "bg-emerald-100 text-emerald-700" :
                        coll.method === "bank" ? "bg-blue-100 text-blue-700" :
                        "bg-purple-100 text-purple-700"
                      }`}>
                        {coll.method === "cash" ? "Nakit" : coll.method === "bank" ? "Banka" : "Kredi Kartı"}
                      </span>
                    </div>
                    <div className="flex items-center justify-end">
                      <span className="font-semibold text-emerald-600 tabular-nums">
                        +₺{coll.amount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Makbuz No</TableHead>
                      <TableHead className="font-semibold">Müşteri</TableHead>
                      <TableHead className="font-semibold">Tarih</TableHead>
                      <TableHead className="font-semibold">Yöntem</TableHead>
                      <TableHead className="font-semibold text-right">Tutar</TableHead>
                      <TableHead className="font-semibold text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCollections.map((coll) => (
                      <TableRow key={coll.id}>
                        <TableCell className="font-mono text-sm">{coll.receipt_no}</TableCell>
                        <TableCell className="font-medium">{coll.customer_name}</TableCell>
                        <TableCell>{coll.date}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            coll.method === "cash" ? "bg-emerald-100 text-emerald-700" :
                            coll.method === "bank" ? "bg-blue-100 text-blue-700" :
                            "bg-purple-100 text-purple-700"
                          }`}>
                            {coll.method === "cash" ? "Nakit" : coll.method === "bank" ? "Banka" : "Kredi Kartı"}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-semibold tabular-nums text-emerald-600">
                          +₺{coll.amount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => handlePrint(coll)}>
                              <Printer className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(coll.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <Receipt className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">{searchTerm ? "Arama sonucu bulunamadı" : "Henüz tahsilat yok"}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Print Dialog */}
      <Dialog open={printDialogOpen} onOpenChange={setPrintDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Tahsilat Makbuzu</DialogTitle>
          </DialogHeader>
          {selectedCollection && (
            <div className="print-container p-6 bg-white border rounded-lg">
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold">TAHSİLAT MAKBUZU</h2>
                <p className="text-slate-500">{selectedCollection.receipt_no}</p>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-500">Müşteri:</span>
                  <span className="font-semibold">{selectedCollection.customer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Tarih:</span>
                  <span className="font-semibold">{selectedCollection.date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Ödeme Yöntemi:</span>
                  <span className="font-semibold">
                    {selectedCollection.method === "cash" ? "Nakit" : 
                     selectedCollection.method === "bank" ? "Banka" : "Kredi Kartı"}
                  </span>
                </div>
                <div className="border-t pt-3 mt-3">
                  <div className="flex justify-between text-lg">
                    <span className="font-semibold">Tutar:</span>
                    <span className="font-bold text-emerald-600">
                      ₺{selectedCollection.amount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-3 no-print">
            <Button variant="outline" onClick={() => setPrintDialogOpen(false)}>Kapat</Button>
            <Button onClick={() => window.print()} className="bg-slate-900 hover:bg-slate-800">
              <Printer className="h-4 w-4 mr-2" /> Yazdır
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Collections;
